/**
 * Сериализация проекта для сохранения и обмена файлами.
 *
 * Формат намеренно простой: проект как есть в JSON, который можно положить
 * в localStorage, отдать на бэкенд или сохранить файлом.
 */
import {
	ITEM_LAYERS,
	LAYERS,
	type PlanLayers,
	getPlanLayer,
	hasCableEnd,
	hasPipeEnd,
	withDefaultLayers,
	type ItemsLayer,
} from "@/shared/plan";
import { DEFAULT_PROJECT_VIEW_3D, createProject, type Project } from "@/shared/store";
import { dehydrateUnderlayImage, resolveUnderlayImage } from "@/shared/store/layers";

export interface ProjectFile {
	savedAt: string;
	project: Project;
}

/** Проект с вынесенной картинкой подложки: Base64 хранится в отдельном JSON браузера, а не в теле проекта. */
export const dehydrateProject = (project: Project): Project => {
	const image = project.layers.underlay.image;
	if (!image) return project;
	return {
		...project,
		layers: {
			...project.layers,
			underlay: {
				...project.layers.underlay,
				image: dehydrateUnderlayImage(image),
			},
		},
	};
};

export const projectFile = (project: Project, savedAt = new Date().toISOString()): ProjectFile => ({
	savedAt,
	project: dehydrateProject(project),
});

export const serializeProject = (project: Project): string => JSON.stringify(projectFile(project));

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

/**
 * Проверка целостности: все слои на месте, ссылки между объектами ведут
 * на существующие. Это защита от повреждённого или чужого файла, а не миграция.
 */
const validateLayers = (layers: unknown): layers is PlanLayers => {
	if (!isRecord(layers)) return false;

	try {
		const planLayers = layers as unknown as PlanLayers;

		// Check all layers exist
		for (const { kind } of LAYERS) {
			const layer = getPlanLayer(planLayers, kind);
			if (!layer || layer.kind !== kind || typeof layer.visible !== "boolean") return false;
		}

		// Check walls layer (WallGraph)
		const wallsLayer = planLayers.walls;
		if (
			!isRecord(wallsLayer.nodes) ||
			!isRecord(wallsLayer.walls) ||
			!isRecord(wallsLayer.openings)
		)
			return false;
		for (const wall of Object.values(wallsLayer.walls)) {
			if (!wallsLayer.nodes[wall.a] || !wallsLayer.nodes[wall.b]) return false;
		}
		for (const opening of Object.values(wallsLayer.openings)) {
			if (!wallsLayer.walls[opening.wallId]) return false;
		}

		// Check wiring layer
		if (!isRecord(planLayers.wiring.items)) return false;
		if (!isRecord(planLayers.wiring.groups)) return false;

		// Концы кабелей — приборы на стенах или светильники на потолке слоя электрики
		const electrical = planLayers.electrical;
		if (!isRecord(electrical.fixtures) || !isRecord(electrical.items)) return false;
		for (const cable of Object.values(planLayers.wiring.items)) {
			if (!hasCableEnd(electrical, cable.from) || !hasCableEnd(electrical, cable.to)) return false;
			if (!Array.isArray(cable.points)) return false;
		}

		// Концы труб — точки водоснабжения на стенах слоя сантехники
		if (!isRecord(planLayers.pipes.items)) return false;
		const plumbing = planLayers.plumbing;
		if (!isRecord(plumbing.fixtures)) return false;
		for (const pipe of Object.values(planLayers.pipes.items)) {
			if (!hasPipeEnd(plumbing, pipe.from) || !hasPipeEnd(plumbing, pipe.to)) return false;
			if (!Array.isArray(pipe.points)) return false;
		}

		// Отделка стен: запись на каждую сторону стены, ссылка на элемент каталога строкой
		if (!isRecord(planLayers.finishes.items)) return false;
		for (const finish of Object.values(planLayers.finishes.items)) {
			if (typeof finish.finish !== "string" || (finish.side !== 1 && finish.side !== -1))
				return false;
		}

		// Check item layers
		for (const kind of ITEM_LAYERS) {
			const layer = getPlanLayer(planLayers, kind) as ItemsLayer;
			if (!isRecord(layer.fixtures) || !isRecord(layer.items)) return false;
			for (const fixture of Object.values(layer.fixtures)) {
				if (!wallsLayer.walls[fixture.wallId] || !isRecord(fixture.ref)) return false;
			}
			for (const item of Object.values(layer.items)) {
				if (!isRecord(item.ref)) return false;
			}
		}

		return true;
	} catch {
		return false;
	}
};

/** Разбирает файл проекта. Возвращает null, если формат не наш или повреждён. */
export const parseProjectFile = (raw: string): Project | null => {
	try {
		const data = JSON.parse(raw) as Partial<ProjectFile>;
		if (!isRecord(data) || !isRecord(data.project)) return null;
		const source = data.project as Partial<Project>;
		// Файл, сохранённый до появления слоя, получает его пустым
		const layers = isRecord(source.layers) ? withDefaultLayers(source.layers) : null;
		if (!layers || !validateLayers(layers)) return null;
		const hydratedLayers: PlanLayers = {
			...layers,
			underlay: {
				...layers.underlay,
				image: resolveUnderlayImage(layers.underlay.image),
			},
		};
		const view3d = isRecord(source.view3d) ? source.view3d : null;
		return createProject({
			id: typeof source.id === "string" ? source.id : undefined,
			name: typeof source.name === "string" ? source.name : undefined,
			description: typeof source.description === "string" ? source.description : "",
			layers: hydratedLayers,
			view3d:
				view3d !== null &&
				typeof view3d.sunAzimuth === "number" &&
				typeof view3d.shadows === "boolean" &&
				typeof view3d.sourceLights === "boolean"
					? {
							sunAzimuth: ((Math.round(view3d.sunAzimuth) % 360) + 360) % 360,
							sunElevation:
								typeof view3d.sunElevation === "number"
									? Math.min(75, Math.max(10, Math.round(view3d.sunElevation)))
									: DEFAULT_PROJECT_VIEW_3D.sunElevation,
							shadows: view3d.shadows,
							sourceLights: view3d.sourceLights,
						}
					: { ...DEFAULT_PROJECT_VIEW_3D },
		});
	} catch {
		return null;
	}
};

export const parseProjectData = (data: unknown): Project | null =>
	parseProjectFile(JSON.stringify(data));
